/**
 * PostHog Event Emitter Utility
 * Used for China/global web and app WebView (global PostHog project for app).
 */
import isNonNumericEmail from "helpers/isNonNumericEmail";
import { getEngageAttributionProps } from "./engageAttribution";
import { getPostHogConfig, getPostHogEventContext, isPostHogEnabled } from "./posthogConfig";

export type PostHogIdentifyFunnel = "login" | "lead" | "book_form" | "payment";

let posthogInstance: any = null;

export const setPostHogInstance = (instance: any) => {
  posthogInstance = instance;
};

export const getPostHogInstance = async (): Promise<any> => {
  if (!isPostHogEnabled()) {
    return null;
  }

  if (posthogInstance) {
    return posthogInstance;
  }

  try {
    // eslint-disable-next-line import/no-extraneous-dependencies
    const posthogModule = await import("posthog-js");
    return posthogModule.default;
  } catch {
    return null;
  }
};

interface PostHogEventParams {
  action: string;
  category?: string;
  label?: string;
  value?: string | number;
  [key: string]: any;
}

/** Super properties applied to every capture (manual + autocapture). */
export function getPostHogSuperProperties(): Record<string, string | number | boolean> {
  const { source } = getPostHogConfig();
  return {
    ...getEngageAttributionProps(),
    ...getPostHogEventContext(),
    source,
  };
}

/** Re-register when Engage URL params / cookie change so autocapture stays attributed. */
export async function registerPostHogSuperProperties(): Promise<void> {
  if (!isPostHogEnabled()) {
    return;
  }

  try {
    const ph = await getPostHogInstance();
    if (ph?.register) {
      ph.register(getPostHogSuperProperties());
    }
  } catch (error) {
    console.error("PostHog super property registration failed:", error);
  }
}

export const posthogEventEmitter = async ({
  action,
  category = "",
  label = "",
  value = "",
  ...additionalProps
}: PostHogEventParams): Promise<void> => {
  if (!isPostHogEnabled() || !action) {
    return;
  }

  try {
    const ph = await getPostHogInstance();
    if (!ph) {
      return;
    }

    const eventProperties = {
      ...(category && { event_category: category }),
      ...(label && { event_label: label }),
      ...(value !== "" && value !== undefined && { value }),
      ...additionalProps,
      ...getPostHogSuperProperties(),
    };

    ph.capture(action, eventProperties);
  } catch (error) {
    console.error("PostHog event emission failed:", error);
  }
};

/** Identify user in PostHog using email as distinct id (skips invalid / numeric-only local parts). */
export const identifyPostHogUserByEmail = async (
  email: string | undefined | null,
  properties?: Record<string, any>,
): Promise<void> => {
  const normalizedEmail = typeof email === "string" ? email.trim() : "";
  if (!normalizedEmail || !isNonNumericEmail(normalizedEmail)) {
    return;
  }

  await posthogIdentify(normalizedEmail, {
    email: normalizedEmail,
    ...properties,
  });
};

export const posthogIdentify = async (
  userId: string,
  userProperties?: Record<string, any>,
): Promise<void> => {
  if (!isPostHogEnabled()) {
    return;
  }

  try {
    const ph = await getPostHogInstance();
    if (!ph) {
      return;
    }

    ph.identify(userId, {
      ...getEngageAttributionProps(),
      ...getPostHogEventContext(),
      ...userProperties,
    });
  } catch (error) {
    console.error("PostHog identify failed:", error);
  }
};

export const posthogReset = async (): Promise<void> => {
  if (!isPostHogEnabled()) {
    return;
  }

  try {
    const ph = await getPostHogInstance();
    if (!ph) {
      return;
    }

    ph.reset();
  } catch (error) {
    console.error("PostHog reset failed:", error);
  }
};

export default posthogEventEmitter;
